import { lazy, ComponentType, LazyExoticComponent } from 'react';

import { logError } from '@/utils/errorLogger';

// Cache for lazy components to prevent recreation during HMR
const lazyComponentCache = new Map<string, LazyExoticComponent<ComponentType>>();

/**
 * Enhanced lazy loading with retry logic for failed imports
 * @param importFunc - The dynamic import function
 * @param label - Component label for logging
 * @param retries - Number of retry attempts (default: 3)
 * @param t - Translation function (optional)
 * @returns Lazy-loaded component
 */
export const createLazyComponent = (
  importFunc: () => Promise<{ default: ComponentType }>,
  label?: string,
  retries: number = 3,
  t?: (page: string, context: string, fallback: string) => string
): LazyExoticComponent<ComponentType> => {
  const componentLabel =
    label ?? (t ? t('lazy_loading', 'component_fallback', 'LazyComponent') : 'LazyComponent');

  // Return cached component if available (prevents recreation during HMR)
  if (lazyComponentCache.has(componentLabel)) {
    return lazyComponentCache.get(componentLabel)!;
  }

  const lazyComponent = lazy(() => {
    const load = async (attemptsLeft = retries) => {
      try {
        // Add a small delay during HMR to prevent race conditions
        if (import.meta.hot && attemptsLeft === retries) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        return await importFunc();
      } catch (error) {
        logError(error, {
          source: `lazy:${componentLabel}`,
          context: {
            attemptsLeft,
            retries,
          },
          severity: attemptsLeft > 0 ? 'warn' : 'error',
        });

        // Don't retry during HMR as it might cause infinite loops
        if (import.meta.hot && attemptsLeft < retries) {
          throw error;
        }

        if (attemptsLeft > 0) {
          // Wait a bit before retrying, with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, retries - attemptsLeft), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          return load(attemptsLeft - 1);
        }

        // If all retries failed, throw a more descriptive error
        const finalError = new Error(
          `Failed to load ${componentLabel} after ${retries} attempts. This might be due to a network issue or outdated cache. Please refresh the page. Original error: ${error instanceof Error ? error.message : String(error)}`
        );
        throw finalError;
      }
    };
    return load();
  });

  // Cache the component
  lazyComponentCache.set(
    componentLabel,
    lazyComponent
  );

  return lazyComponent;
};

// Clear cache on HMR updates
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    lazyComponentCache.clear();
  });
}
